<template>
    <div v-if="!loading && subscriptions" class="w-full flex flex-column gap-3">
        <div class="flex gap-2">
            <Button label="Экспорт" icon="pi pi-download" @click="exportCSV" severity="secondary" size="small" class="flex"/>
            <Button label="Обновить" icon="pi pi-refresh" @click="loadAttempts" severity="secondary" size="small" class="flex"/>
            <Button label="Сбросить" icon="pi pi-times" @click="resetFilters" severity="secondary" size="small" class="flex"/>
        </div>

        <DataTable
            ref="dt"
            :value="subscriptions"
            responsiveLayout="scroll"
            v-model:filters="lazyParams.filters"
            @page="onPage($event)"
            @sort="onSort($event)"
            @filter="onFilter($event)"
            :globalFilterFields="globalFields"
            lazy
            filterDisplay="menu"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            :loading="loading">

            <template #header>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                </div>
            </template>

            <Column field="id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="subscription_id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Подписка</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.subscription_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.subscription_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                        <Button @click="goToSubscription(data.subscription_id)" icon="pi pi-external-link" iconPos="right" text severity="info" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="billing_period_key" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Период биллинга</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ data.billing_period_key || '-' }}</span>
                </template>
            </Column>

            <Column field="amount" sortable>
                <template #header>
                    <span class="text-xs font-medium">Сумма</span>
                </template>
                <template #body="{ data }">
                    <span v-if="data.amount !== null && data.amount !== undefined" class="white-space-nowrap flex flex-row gap-1 align-items-center">
                        <span class="white-space-nowrap">
                            {{ getFormatAmount(data.amount, data.currency || 'rub') }} {{ (data.currency || '').toUpperCase() }}
                        </span>
                        <Button @click="copy(`${data.amount}`)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </span>
                </template>
            </Column>

            <Column field="status" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Статус</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="statusOptions" placeholder="Все" class="p-column-filter" :showClear="true">
                        <template #option="slotProps">
                            <Tag :value="getStatusTitle(slotProps.option)" class="text-xs" :severity="getStatusSeverity(slotProps.option)" />
                        </template>
                    </Select>
                </template>
                <template #body="{ data }">
                    <Tag :value="getStatusTitle(data.status)" class="text-xs" :severity="getStatusSeverity(data.status)" />
                </template>
            </Column>

            <Column field="bank_payment_id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Bank Payment ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.bank_payment_id" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.bank_payment_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.bank_payment_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                    <span v-else class="text-xs">-</span>
                </template>
            </Column>

            <Column field="order_id" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Order ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.order_id" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.order_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.order_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                        <Button @click="goToOrder(data.order_id)" icon="pi pi-external-link" iconPos="right" text severity="info" class="text-xs" size="small"/>
                    </div>
                    <span v-else class="text-xs">-</span>
                </template>
            </Column>

            <Column field="error_code" sortable filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Код ошибки</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.error_code" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs">{{ data.error_code }}</span>
                        <Button @click="copy(data.error_code)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                    <span v-else class="text-xs">-</span>
                </template>
            </Column>

            <Column field="error_message" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Ошибка</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.error_message" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 18rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.error_message }}
                            </span>
                        </span>
                        <Button @click="copy(data.error_message)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                    <span v-else class="text-xs">-</span>
                </template>
            </Column>

            <Column field="created_at" filterField="created_at" sortable dataType="date" :showFilterMatchModes="false" :showFilterOperator="false">
                <template #header>
                    <span class="text-xs font-medium">Дата создания</span>
                </template>
                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" showTime hourFormat="24" fluid/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.created_at) }}</span>
                </template>
            </Column>

            <template #footer>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                </div>
            </template>
        </DataTable>

        <Paginator
            v-model:first="lazyParams.first"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            template="FirstPageLink  PageLinks LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[10, 20, 30, 50, 100, 200, 300, 400, 500, 1000, 2000, 3000, 4000, 5000]"
            @page="onPage"
        />
    </div>
</template>

<script>
import Helper from '@/assets/js/helper.js'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'

export default {
    props: {
        subscriptions: {
            type: Array,
            required: true
        },
        users: {
            type: Array,
            default: () => []
        },
        totalSubscriptions: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        defaultParams: {
            type: Object,
            default: () => ({})
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['filter', 'lazyParams'],
    data() {
        return {
            lazyParams: this.defaultParams,
            sort: {
                field: 'created_at',
                order: -1
            },
            statusSeeds: ['pending', 'processing', 'paid', 'failed', 'canceled', 'expired'],
            globalFields: [
                'id',
                'subscription_id',
                'billing_period_key',
                'amount',
                'status',
                'bank_payment_id',
                'order_id',
                'error_code',
                'error_message',
                'created_at'
            ],
            dt: null
        }
    },
    computed: {
        statusOptions() {
            const dynamicStatuses = (this.subscriptions || [])
                .map(item => item.status)
                .filter(Boolean)
            return [...new Set([...this.statusSeeds, ...dynamicStatuses])]
        }
    },
    methods: {
        resetFilters() {
            this.lazyParams.filters = {
                id: { value: '', matchMode: 'contains' },
                subscription_id: { value: '', matchMode: 'contains' },
                billing_period_key: { value: '', matchMode: 'contains' },
                status: { value: '', matchMode: 'contains' },
                bank_payment_id: { value: '', matchMode: 'contains' },
                order_id: { value: '', matchMode: 'contains' },
                error_code: { value: '', matchMode: 'contains' },
                error_message: { value: '', matchMode: 'contains' },
                created_at: {
                    operator: FilterOperator.AND,
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
                }
            }
            this.$emit('filter', this.lazyParams.filters)
        },
        getStatusSeverity(status) {
            return this.helper.getStatusSeverity(status)
        },
        getStatusTitle(status) {
            return this.helper.getStatusTitle(status)
        },
        formatDate(dateStr) {
            return this.helper.formatDate(dateStr)
        },
        formatNullableDate(dateStr) {
            if (!dateStr) return '-'
            return this.formatDate(dateStr)
        },
        formatAmount(amount) {
            return this.helper.format_amount(amount, 'rub')
        },
        copy(text) {
            if (text === null || text === undefined || text === '') return
            const value = typeof text === 'string' ? text : String(text)
            this.helper.copy_clipboard(value)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: value, life: 1000 })
        },
        onPage(event) {
            this.lazyParams.first = event.first
            this.lazyParams.rows = event.rows
            this.lazyParams.page = event.page
            this.lazyParams.pageCount = event.pageCount
            this.$emit('lazyParams', this.lazyParams)
        },
        onSort(event) {
            if (event.sortField && event.sortOrder) {
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.$emit('lazyParams', this.lazyParams)
        },
        onFilter(params) {
            this.lazyParams.filters = params.filters
            this.$emit('filter', params.filters)
        },
        loadAttempts() {
            this.$emit('lazyParams', this.lazyParams)
        },
        exportCSV() {
            this.$refs.dt.exportCSV()
        },
        goToSubscription(id){
            if(!id) return
            window.open(`/admin/subscriptions/?id=${id}`, '_blank')
        },
        goToOrder(id){
            if(!id) return
            window.open(`/admin/orders/?id=${id}`, '_blank')
        },
        getFormatAmount(value, currency, digits = 2) {
            return this.helper.format_amount(value, currency, digits)
        },
    },
    async beforeCreate() {
        this.helper = new Helper()
    },
    mounted() {
        this.dt = this.$refs.dt
    }
}
</script>
